import { useSearchParams } from '@/shared/modern-router';

/**
 * Project-wide QA/demo convention for forcing UI states from the URL, with no backend data.
 *
 * Reads the `?force=` query param — a comma-separated list of `key` or `key:variant` tokens —
 * and returns the variant for `key`: the variant string, `''` when the key is present without a
 * variant (bare `?force=key`), or `undefined` when the key is absent.
 *
 * OFF BY DEFAULT (no param → `undefined`) → zero production impact. Read-only / visual only:
 * consumers use the returned variant to render fake/forced UI; never to mutate data.
 *
 * @example ?force=photo:mixed     → useForcedUi('photo') === 'mixed'
 * @example ?force=photo           → useForcedUi('photo') === ''
 * @example ?force=photo:ok,banner → useForcedUi('photo') === 'ok', useForcedUi('banner') === ''
 */
export const useForcedUi = (key: string): string | undefined => {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('force');

  if (!raw) {
    return undefined;
  }

  const match = raw
    .split(',')
    .map(token => token.trim())
    .find(token => token === key || token.startsWith(`${key}:`));

  if (match === undefined) {
    return undefined;
  }

  const [, variant] = match.split(':');

  return variant ?? '';
};
